/**
 * レーン列の選択の表示と解除 — いま何で絞っているかを 1 行で出す。
 *
 * **選んだものを画面に出す。** レーンのカードは選択中の枠で示すが、スクロールして見えない
 * 位置にあると「何で絞られているのか」が画面から読めなくなる。押せば外れる札にして、
 * 解除の導線も同じ場所に置く（ラフ `overview.html` の `bar`）。
 *
 * **何も選んでいないときは何も描かない**（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に」）。
 * 前は「選択: なし」を常に出していたが、選んでいない時に読む物は無い。選んだ時だけ札と
 * 「選択を外す」を出し、「選択:」の前置きも置かない（札そのものが「レーン: 名前 ×」と言う）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン列でいま絞っている条件を札で出し、1 つずつ・またはまとめて解除できるようにする。
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';

/** 絞っている条件 1 つ。 @graph-connects none */
export interface LaneSelectionChip {
  /** 条件の鍵（レーンの種別） */
  key: string;
  /** レーンの見出し */
  lane: string;
  /** 選んでいるカードの見出し */
  label: string;
}

/** `LaneSelectionBar` の props。 */
export interface LaneSelectionBarProps {
  /** 絞っている条件。空なら何も描かない */
  chips: readonly LaneSelectionChip[];
  /** 条件 1 つを外すときに呼ぶ */
  onClear: (key: string) => void;
  /** すべて外すときに呼ぶ */
  onClearAll: () => void;
}

/** 札 1 つの見た目。 @graph-connects none */
const CHIP_CLASS =
  'border border-[var(--color-border)] px-2.5 py-0.5 text-xs font-bold text-[var(--color-text-secondary)]';

/**
 * 絞り込みの札の列を描く。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞っている条件を札で並べ、1 つずつ・またはまとめて解除する操作を出す。何も選んでいなければ何も描かない。
 * @graph-connects none
 */
export function LaneSelectionBar({ chips, onClear, onClearAll }: LaneSelectionBarProps) {
  const { t } = useTranslation();

  if (chips.length === 0) return null;

  return (
    <div data-lane-selection className="flex flex-wrap items-center gap-2 pb-3">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          data-lane-selection-clear={chip.key}
          onClick={() => {
            onClear(chip.key);
          }}
          className={`${CHIP_CLASS} cursor-pointer border-[var(--color-accent-primary)] text-[var(--color-text-primary)]`}
        >
          {t('overview.selectionChip', { lane: chip.lane, label: chip.label })}
        </button>
      ))}
      <button
        type="button"
        data-lane-selection-clear="all"
        onClick={onClearAll}
        className={`${CHIP_CLASS} cursor-pointer hover:text-[var(--color-text-primary)]`}
      >
        {t('overview.selectionClearAll')}
      </button>
    </div>
  );
}
